/**
 * SuperAdmin review queue for tenant plan-change requests.
 *
 * Routed at `/platform/plan-requests`. Lists every PENDING request,
 * lets SUPER_ADMIN approve (with optional custom pricing override) or
 * reject (with a reason). Approval fires the tenant-service flow which
 * emits `subscription.upgraded` (or downgraded) and — for Professional
 * transitions — auto-enables/disables Org Brain via the ai-engine
 * Kafka consumer.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { apiClient } from '@services/api-client';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface PlanRequest {
  id: string;
  tenantId: string;
  fromPlan: string;
  toPlan: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  // Org-admin's chosen billing cycle at request time. Server-side default
  // is 'MONTHLY' for any pre-migration row, so this is non-null on every
  // row coming back from /admin/plan-requests post-deploy.
  billingCycle: 'MONTHLY' | 'YEARLY';
  requestedByUserId: string | null;
  requestedAt: string;
  rejectedReason: string | null;
  contractRef: string | null;
  notes: string | null;
}

export default function PlatformPlanRequestsPage() {
  const [requests, setRequests] = useState<PlanRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PlanRequest | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get('/admin/plan-requests');
      setRequests(res.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Plan change requests</h1>
          <p className="text-sm text-gray-600 mt-1">
            {requests.length} pending request{requests.length === 1 ? '' : 's'}
            . Approving publishes <code>subscription.upgraded</code> and (on
            Professional transitions) auto-enables Org Brain.
          </p>
        </div>
        <Button variant="outline" onClick={refresh} disabled={loading}>
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="p-4 bg-red-50 text-red-800 text-sm">{error}</Card>
      )}

      {loading && <Spinner />}

      {!loading && requests.length === 0 && !error && (
        <Card className="p-6 text-sm text-gray-500 text-center">
          No pending plan-change requests. When a tenant owner requests an
          upgrade from Organization Settings → General, it will appear here.
        </Card>
      )}

      {!loading && requests.length > 0 && (
        <Card className="overflow-hidden">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-2">Tenant</th>
                <th className="px-4 py-2">Transition</th>
                <th className="px-4 py-2">Requested cycle</th>
                <th className="px-4 py-2">Requested</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs">{r.tenantId}</td>
                  <td className="px-4 py-2 capitalize">
                    {r.fromPlan} → {r.toPlan}
                  </td>
                  <td className="px-4 py-2">
                    <BillingCyclePill cycle={r.billingCycle ?? 'MONTHLY'} />
                  </td>
                  <td className="px-4 py-2 text-gray-600">
                    {new Date(r.requestedAt).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button onClick={() => setSelected(r)}>Review</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {selected && (
        <ReviewPanel
          request={selected}
          onClose={() => setSelected(null)}
          onResolved={() => {
            setSelected(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

interface ReviewProps {
  request: PlanRequest;
  onClose: () => void;
  onResolved: () => void;
}

function ReviewPanel({ request, onClose, onResolved }: ReviewProps) {
  const requestedCycle = request.billingCycle ?? 'MONTHLY';
  const requestedInterval: 'monthly' | 'annual' =
    requestedCycle === 'YEARLY' ? 'annual' : 'monthly';

  const [contractRef, setContractRef] = useState('');
  const [notes, setNotes] = useState('');
  const [preferredCurrency, setPreferredCurrency] = useState('');
  // SuperAdmin can override the requester's cycle at approve time. Default
  // to whatever the org-admin asked for so the common path is one click;
  // the override is a deliberate action.
  const [overrideCycle, setOverrideCycle] = useState<'' | 'MONTHLY' | 'YEARLY'>('');
  const [useOverride, setUseOverride] = useState(false);
  const [overrideCurrency, setOverrideCurrency] = useState('USD');
  const [overrideMonthly, setOverrideMonthly] = useState('');
  const [overrideAnnual, setOverrideAnnual] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const approveBody = useMemo(() => {
    const interval: 'monthly' | 'annual' =
      overrideCycle === 'YEARLY'
        ? 'annual'
        : overrideCycle === 'MONTHLY'
          ? 'monthly'
          : requestedInterval;
    const body: any = {
      contractRef: contractRef || undefined,
      notes: notes || undefined,
      preferredCurrency: preferredCurrency || undefined,
      interval,
    };
    if (
      useOverride &&
      overrideCurrency &&
      overrideMonthly &&
      overrideAnnual
    ) {
      body.customOverride = {
        currency: overrideCurrency.toUpperCase(),
        monthlyAmount: overrideMonthly,
        annualAmount: overrideAnnual,
        interval,
      };
    }
    return body;
  }, [
    contractRef,
    notes,
    preferredCurrency,
    overrideCycle,
    requestedInterval,
    useOverride,
    overrideCurrency,
    overrideMonthly,
    overrideAnnual,
  ]);

  async function approve() {
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post(
        `/admin/plan-requests/${request.id}/approve`,
        approveBody,
      );
      onResolved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    if (!confirm('Reject this request? The tenant owner will be notified.')) return;
    setBusy(true);
    setErr(null);
    try {
      await apiClient.post(`/admin/plan-requests/${request.id}/reject`, {
        reason: rejectReason || undefined,
      });
      onResolved();
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Reject failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 className="text-lg font-semibold">Review plan change</h2>
          <p className="text-sm text-gray-600">
            Tenant <span className="font-mono">{request.tenantId}</span> •
            <span className="capitalize"> {request.fromPlan} → {request.toPlan}</span>
          </p>
          <p className="text-sm text-gray-700 mt-1 flex items-center gap-2">
            Requested billing cycle:{' '}
            <BillingCyclePill cycle={requestedCycle} />
            <span className="text-xs text-gray-500">
              (you can override below)
            </span>
          </p>
        </header>

        <div className="grid grid-cols-2 gap-3 text-sm">
          <Input label="Contract / PO reference" value={contractRef} onChange={setContractRef} />
          <Input label="Preferred currency (ISO)" value={preferredCurrency} onChange={setPreferredCurrency} placeholder="USD" />
        </div>

        <label className="block text-sm">
          <span className="text-gray-700">Override billing cycle</span>
          <ThemedSelect
            className="mt-1 w-full border rounded px-2 py-1"
            value={overrideCycle}
            onChange={(e) =>
              setOverrideCycle(e.target.value as '' | 'MONTHLY' | 'YEARLY')
            }
          >
            <option value="">
              Use requester's choice ({requestedCycle.toLowerCase()})
            </option>
            <option value="MONTHLY">Force monthly</option>
            <option value="YEARLY">Force yearly</option>
          </ThemedSelect>
        </label>

        <label className="block text-sm">
          <span className="text-gray-700">Notes (internal)</span>
          <textarea
            className="mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
          />
        </label>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={useOverride}
            onChange={(e) => setUseOverride(e.target.checked)}
          />
          <span>Apply a custom per-tenant price (bespoke deal)</span>
        </label>

        {useOverride && (
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Input label="Currency" value={overrideCurrency} onChange={setOverrideCurrency} />
            <Input label="Monthly" value={overrideMonthly} onChange={setOverrideMonthly} placeholder="0.00" />
            <Input label="Annual" value={overrideAnnual} onChange={setOverrideAnnual} placeholder="0.00" />
          </div>
        )}

        <hr className="my-2" />

        <div>
          <label className="block text-sm text-gray-700 mb-1">
            Or reject with reason:
          </label>
          <textarea
            className="mt-1 w-full border rounded px-2 py-1 text-sm"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={2}
            placeholder="Why is this being rejected?"
          />
        </div>

        {err && <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">{err}</div>}

        <footer className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Close
          </Button>
          <Button variant="outline" onClick={reject} disabled={busy}>
            Reject
          </Button>
          <Button onClick={approve} disabled={busy}>
            {busy ? 'Applying…' : 'Approve + apply plan'}
          </Button>
        </footer>
      </div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="text-gray-700">{label}</span>
      <input
        type="text"
        className="mt-1 w-full border rounded px-2 py-1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </label>
  );
}

// Visual chip for the org-admin's chosen billing cycle. YEARLY uses the
// emerald-on-green palette already used elsewhere on Plan & Billing for
// the "save with yearly" affordance; MONTHLY uses the neutral indigo
// palette so the two are clearly distinguishable in the queue table.
function BillingCyclePill({ cycle }: { cycle: 'MONTHLY' | 'YEARLY' }) {
  const tone =
    cycle === 'YEARLY'
      ? 'bg-emerald-100 text-emerald-800'
      : 'bg-indigo-100 text-indigo-800';
  return (
    <span
      className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {cycle === 'YEARLY' ? 'Yearly' : 'Monthly'}
    </span>
  );
}
